import React from 'react';
import {useIntl} from 'react-intl';
import {Heading, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import Styled from 'styled-components';

const tokens = tokensForThemeProvider.sky.sys;

/* A plain div rather than the DS FlexColumn, matching `EmptyReportState` and `ReportErrorState`:
 * FlexColumn forwards neither the accessibility role nor `data-cy`, so both hooks below would
 * silently never reach the DOM. The layout it would provide is four lines of CSS. */
const Wrapper = Styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: ${tokens.spacing['200']};
	padding: ${tokens.spacing['800']} ${tokens.spacing['600']};
`;

/**
 * What a saved report route shows when no report page here can render the report.
 *
 * The only surface that renders a project status report is the report builder, and the saved-report
 * route reaches this state whenever it cannot hand the reader over to it: the builder's entitlement
 * excludes them, the report arrived through an external share link (where the hand-over is skipped
 * because the builder route has no concept of a share), or the report was not authored in the
 * builder at all. The copy therefore names the surface without asserting which of those it is.
 *
 * Naming the surface rather than the report's type is deliberate: the type is not what the reader is
 * missing, and the same dead end catches any type this conductor has no page for.
 *
 * Deliberately not the shared `EmptyState`: that component keys off an enum of page identities with
 * per-page artwork and calls to action, which is a heavier thing than one honest sentence, and none
 * of its cases describe a report that opens somewhere else.
 */
const UnopenableReportState = (): React.JSX.Element => {
	const {formatMessage} = useIntl();

	return (
		// `status` rather than `alert`: the reader did nothing wrong and nothing failed, so an
		// assertive announcement would misdescribe it. It is not load-bearing on its own, though -
		// a live region that mounts with its text already in place is commonly not announced at
		// all, so the heading below is what a screen reader actually navigates to.
		<Wrapper role={'status'} data-cy={'report-unopenable'}>
			{/* The heading role rides this element rather than the design system's Heading, which
			    renders a div and whose props are size, color, noWrap and children only - it accepts
			    neither `as` nor a role. Without this the page offers a screen reader no landmark and
			    no announcement, which is the silence this state exists to end. Drop it once the
			    design system's Heading can render a real heading element. */}
			<div role={'heading'} aria-level={2}>
				<Heading size={'2'}>{formatMessage({id: 'report.unopenable.title'})}</Heading>
			</div>
			<Text size={'2'} color={'auxiliary'}>
				{formatMessage({id: 'report.unopenable.description'})}
			</Text>
		</Wrapper>
	);
};

export default UnopenableReportState;
